import { useEffect, useState } from "react";
import { motion } from "motion/react";
import ChannelDisplay from "@/components/ChannelDisplay";
import type { Video } from "@/types/video";
import type { RandomMode } from "@/types/video";

interface TopBarProps {
  video: Video | null;
  loading: boolean;
  switching: boolean;
  unknownMode: boolean;
  mode: RandomMode;
  year: number | null;
  visible: boolean;
  onOpenHistory: () => void;
  onOpenFavorites: () => void;
  onOpenMode: () => void;
  onOpenArchive: () => void;
}

export default function TopBar({
  video,
  loading,
  switching,
  unknownMode,
  mode,
  year,
  visible,
  onOpenHistory,
  onOpenFavorites,
  onOpenMode,
  onOpenArchive,
}: TopBarProps) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = now.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return (
    <motion.div
      className={`absolute top-20 sm:top-24 inset-x-0 z-30 pointer-events-none transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
      data-ui-zone
    >
      <div className="flex justify-between items-start p-4 sm:p-6 safe-top safe-left safe-right">
        {/* Left: brand + channel */}
        <div className="acg-frame pointer-events-auto flex items-start gap-4 sm:gap-6 px-4 py-2.5 bg-white/85">
          <button
            onClick={() => (window.location.href = "/")}
            className="font-display font-bold tracking-widest3 text-base sm:text-lg hover:text-signal transition-colors"
            aria-label="返回首页"
          >
            <span className="flex items-center gap-2"><img src="/bilibili-color.svg" alt="" className="w-5 h-5" />BILITIME</span>
          </button>
          <div className="hidden sm:block w-px h-8 bg-phosphor/15 mt-1" />
          <ChannelDisplay
            video={video}
            loading={loading}
            switching={switching}
            unknownMode={unknownMode}
            mode={mode}
            year={year}
          />
        </div>

        {/* Right: actions + clock */}
        <div className="acg-frame pointer-events-auto flex items-start gap-4 sm:gap-6 px-4 py-2.5 bg-white/85">
          <div className="hidden sm:flex flex-col items-end gap-1 font-mono text-[10px] tracking-widest2">
            <button
              onClick={onOpenHistory}
              className="text-static hover:text-signal transition-colors"
            >
              HISTORY [H]
            </button>
            <button
              onClick={onOpenFavorites}
              className="text-static hover:text-signal transition-colors"
            >
              MY TAPES [L]
            </button>
            <button
              onClick={onOpenMode}
              className="text-static hover:text-signal transition-colors"
            >
              CHANNEL [R]
            </button>
            <button
              onClick={onOpenArchive}
              className="text-static hover:text-signal transition-colors"
            >
              ARCHIVE [O]
            </button>
          </div>
          <div className="font-mono text-xs tracking-widest2 text-static tabular-nums">
            {timeStr}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
